import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { validateFields } from './utils/validation';
import PageHeader from './components/PageHeader';
import DialogAlert from './components/DialogAlert';
import DialogFooter from './components/DialogFooter';
import { DividerRow, FileField, TextField } from './components/FormFields';
import { useAnimatedClose } from './hooks/useAnimatedClose';
import BrandCreationPreview from './components/BrandCreationPreview';
import MarkdownEditorField from "./components/MarkdownEditorField";
import { getBrandById } from './hooks/useBrands';
import useMarkets from './hooks/useMarkets';
import { normalizeBrandWebsiteUrl } from './utils/brandWebsiteUrl';
import { useImageUpload } from './hooks/useImageUpload';
import { ToastInline } from './components/ToastNotifications';
import type { BrandRecord, MarketRecord } from './types/index';

type SelectableMarket = MarketRecord & {
  selected?: boolean;
};

type BrandForm = {
  id?: string;
  name: string;
  displayName: string;
  markets: SelectableMarket[];
  description: string;
  purchaseUrl: string;
  websiteUrl: string;
  tagline: string;
};

type BrandDetailsDialogProps = {
  onClose: () => void;
  onSave: (payload: any, other: any) => void;
  currentOrg: any;
  brandId?: string;
  title: string;
}

export default function BrandDetailsDialog({ onClose, onSave, currentOrg, brandId, title }: BrandDetailsDialogProps) {
  const testPrefix = brandId ? 'edit-brand' : 'add-brand';
  const [loadedBrand, setLoadedBrand] = useState<{
    brand: BrandRecord;
    organizationId: number | string;
    brandId: string;
  } | null>(null);
  const logo = useImageUpload({ organizationId: currentOrg?.id, kind: 'brand' });
  const [form, setForm] = useState<BrandForm>({
    id: brandId,
    name: '',
    displayName: '',
    markets: [],
    description: '',
    purchaseUrl: '',
    websiteUrl: '',
    tagline: '',
  });
  const {
    data: allMarkets,
    error: marketsError,
    isLoading: isLoadingMarkets,
  } = useMarkets({
    organizationId: currentOrg?.id,
    enabled: Boolean(currentOrg?.id),
  });

  useLayoutEffect(() => {
    let cancelled = false;
    const logoContext = logo.reset();
    setForm((current) => ({ ...current, id: brandId }));

    async function getBrand() {
      try {
        const brand = await getBrandById(currentOrg?.id, brandId);
        if (!cancelled && brand) {
          logo.hydrate(logoContext, { id: brand.brandLogoId, url: brand.brandLogoUrl });
          setLoadedBrand({ brand, organizationId: currentOrg.id, brandId });
        }
      } catch (error) {
        if (!cancelled) console.log(error);
      }
    }

    setLoadedBrand(null);
    if (currentOrg?.id && brandId) {
      getBrand();
    }

    return () => {
      cancelled = true;
    };
  }, [currentOrg?.id, brandId]);

  useEffect(() => {
    if (!brandId || !loadedBrand || isLoadingMarkets ||
        loadedBrand.organizationId !== currentOrg?.id || loadedBrand.brandId !== brandId) return;

    const brand = loadedBrand.brand;

    const finalMarkets = marketsError
      ? []
      : allMarkets.map((market) => ({
        ...market,
        selected: brand.marketIds?.some((id) => id == market.id) || false,
      }));

    setForm((current) => ({
      ...current,
      name: brand.name || '',
      markets: finalMarkets,
      displayName: brand.displayName || '',
      purchaseUrl: brand.purchaseUrl || '',
      websiteUrl: brand.websiteUrl || '',
      description: brand.description || '',
      tagline: brand.tagline || '',
    }));
  }, [allMarkets, brandId, currentOrg?.id, isLoadingMarkets, loadedBrand, marketsError]);

  const [errors, setErrors] = useState<Record<string, any>>({});
  const { close, isClosing } = useAnimatedClose(onClose);

  const schema = [
    { name: 'name', required: true, message: 'Brand name is required' },
    { name: 'displayName', required: true, message: 'Display name is required' },
    {
      name: 'markets',
      validate: (markets) => {
        return !markets.some(m => m.selected);
      },
      message: 'Select at least one market'
    },
    {
      name: "purchaseUrl",
      validate: (value) => {
        return !isValidUrl(value);
      },
      message: 'Please enter a valid url'
    },
    {
      name: "websiteUrl",
      validate: (value) => {
        return normalizeBrandWebsiteUrl(value) === null;
      },
      message: 'Please enter a valid url'
    }
  ];


  useEffect(() => {
    if (!currentOrg || isLoadingMarkets || marketsError || brandId) return;
    update('markets', allMarkets.map(m => ({ ...m, selected: true })));
  }, [allMarkets, brandId, currentOrg, isLoadingMarkets, marketsError]);


  const update = <K extends keyof BrandForm>(field: K, value: BrandForm[K]) => {
    setForm((f) => ({ ...f, [field]: value }))
  };

  const handleSave = async () => {
    if (logo.isPending) return;
    const purchaseUrl = form.purchaseUrl?.trim() || '';
    const websiteUrl = normalizeBrandWebsiteUrl(form.websiteUrl || '');
    const { errors: nextErrors, isValid } = validateFields(schema, {
      ...form,
      purchaseUrl,
      websiteUrl: form.websiteUrl?.trim() || '',
    });
    setErrors(nextErrors);
    if (!isValid || websiteUrl === null) return;

    const markets = form.markets.length ? form.markets : [];

    const payload = {
      Name: form.name.trim(),
      DisplayName: form.displayName.trim(),
      MarketIds: markets.filter(m => m.selected).map(m => m.id),
      OrganizationId: currentOrg.id,
      Description: form.description,
      PurchaseUrl: purchaseUrl,
      WebsiteUrl: websiteUrl,
      BrandId: brandId,
      Tagline: form.tagline,
    }

    if (logo.id) {
      payload['BrandLogoId'] = logo.id;
    }
    onSave(payload, null);
  };

  function isValidUrl(string) {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  }

  // Inline markets selector component
  function InlineMarketsSelector({ form, errors, update }: any) {
    const fieldRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const marketFieldRef = useRef<HTMLDivElement | null>(null);
    const dropdownPortalRef = useRef<HTMLDivElement | null>(null);
    const [showMarketDropdown, setShowMarketDropdown] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
      if (!showMarketDropdown) return;
      const el = marketFieldRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom, left: rect.left, width: rect.width });

      function handleDocClick(e: MouseEvent) {
        const target = e.target as Node;
        if (
          dropdownPortalRef.current && dropdownPortalRef.current.contains(target)
        ) return;
        if (marketFieldRef.current && marketFieldRef.current.contains(target)) return;
        setShowMarketDropdown(false);
      }

      function handleKey(e: KeyboardEvent) {
        if (e.key === 'Escape') setShowMarketDropdown(false);
      }

      document.addEventListener('click', handleDocClick);
      document.addEventListener('keydown', handleKey);
      return () => {
        document.removeEventListener('click', handleDocClick);
        document.removeEventListener('keydown', handleKey);
      };
    }, [showMarketDropdown]);

    const selectedMarkets = (form.markets || []).filter((m) => m.selected) || [];

    return (
      <div
        className="field"
        ref={fieldRef}
        style={{ position: 'relative' }}
        data-testid={`${testPrefix}-markets`}
      >
        <div className="label-row">
          <label className="label">Market <span className="required-dot" aria-label="required">*</span></label>
          <span className="markets-count" data-testid={`${testPrefix}-markets-count`}>
            {selectedMarkets.length || 0} Markets Selected
          </span>
        </div>
        <div className="markets-selector-wrapper" ref={dropdownRef} style={{ position: 'relative', overflow: 'visible' }}>
          <div
            ref={marketFieldRef}
            className="markets-inline-selector clickable"
            data-testid={`${testPrefix}-markets-toggle`}
            onClick={() => setShowMarketDropdown(!showMarketDropdown)}
          >
            <div className="markets-selected-pills">
              {(() => {
                const visibleMarkets = selectedMarkets.slice(0, 3);
                const remainingCount = selectedMarkets.length - 3;

                return (
                  <>
                    {selectedMarkets.length === 0 && (
                      <span className="markets-placeholder">Select markets...</span>
                    )}
                    {visibleMarkets.map((market: any) => (
                      <div key={market.id} className="market-pill">
                        <span>{market.code}</span>
                        <button
                          type="button"
                          className="market-pill-close"
                          data-testid={`${testPrefix}-markets-remove-${market.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const updated = form.markets.map((m) =>
                              m.id === market.id ? { ...m, selected: false } : m
                            );
                            update('markets', updated);
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {remainingCount > 0 && (
                      <div className="market-pill market-pill-more">+{remainingCount}</div>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="markets-selector-actions">
              <img src="/icons/search.svg" className="markets-search-icon" alt="Search" />
            </div>
          </div>

          {showMarketDropdown && createPortal(
            <div
              ref={dropdownPortalRef}
              className="markets-dropdown"
              data-testid={`${testPrefix}-markets-dropdown`}
              style={{
                position: 'absolute',
                top: `${dropdownPosition.top + window.scrollY}px`,
                left: `${dropdownPosition.left + window.scrollX}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 9999,
              }}
            >
              <div className="markets-dropdown-content">
                {(() => {
                  const unselectedMarkets = form.markets?.filter((m) => !m.selected) || [];
                  return unselectedMarkets.map((market: any) => (
                    <div
                      key={market.id}
                      className="market-dropdown-item"
                      data-testid={`${testPrefix}-markets-option-${market.id}`}
                      onClick={() => {
                        const updated = form.markets.map((m) =>
                          m.id === market.id ? { ...m, selected: true } : m
                        );
                        update('markets', updated);
                        setShowMarketDropdown(false);
                      }}
                    >
                      <span className="market-code">{market.code}</span>
                    </div>
                  ));
                })()}
                {form.markets?.filter((m: any) => !m.selected).length === 0 && (
                  <div className="market-dropdown-empty" data-testid={`${testPrefix}-markets-empty`}>
                    All markets selected
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
        </div>
        {errors.markets && <div className="error-text" data-testid={`${testPrefix}-markets-error`}>{errors.markets}</div>}
      </div>
    );
  }


  return (
    <div className={`stack page-slide-in${isClosing ? ' page-slide-out' : ''}`} data-testid={testPrefix}>
      <PageHeader
        title={title}
        subtitle="Brands"
        actions={(
          <button className="button secondary" onClick={close} data-testid={`${testPrefix}-close`}>Back</button>
        )}
      />

      <div className="drawer-body">
        <div className="drawer-controls">
          <div className="drawer-controls-card">
            <DialogAlert show={Object.keys(errors).length > 0} testId="brand-details-validation-error" />

            <div className="form-grid">
              <TextField
                label="Brand Name"
                required
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Enter brand name"
                error={errors.name}
                testId={`${testPrefix}-name`}
                errorTestId={`${testPrefix}-name-error`}
              />

              <TextField
                label="Display Name"
                required
                value={form.displayName}
                onChange={(e) => update('displayName', e.target.value)}
                placeholder="Enter display name"
                error={errors.displayName}
                testId={`${testPrefix}-display-name`}
                errorTestId={`${testPrefix}-display-name-error`}
              />
              
              <TextField
                label="Tagline"
                value={form.tagline}
                onChange={(e) => update('tagline', e.target.value)}
                placeholder="Enter tagline"
                error={errors.tagline}
                testId={`${testPrefix}-tagline`}
                errorTestId={`${testPrefix}-tagline-error`}
                          />

              <ToastInline message={logo.error} onDismiss={logo.dismissError} testId={`${testPrefix}-logo-error`} />
              <FileField
                key={logo.fileInputKey}
                label="Logo"
                accept="image/*"
                onChange={(e) => logo.select(e.target.files?.[0])}
                selectedName={logo.name || (logo.id ? 'Current logo' : '')}
                removeAction={logo.remove}
                wrapperClassName="field full-span"
                testId={`${testPrefix}-logo`}
              />

              <DividerRow />

              <div className="field full-span">
                {/* Inline markets selector with portal dropdown */}
                <InlineMarketsSelector
                  form={form}
                  errors={errors}
                  update={update}
                />
              </div>

              <DividerRow />

              <TextField
                label="Purchase URL"
                value={form.purchaseUrl}
                onChange={(e) => update('purchaseUrl', e.target.value)}
                placeholder="Enter Purchase URL"
                error={errors.purchaseUrl}
                testId={`${testPrefix}-purchase`}
                errorTestId={`${testPrefix}-purchase-error`}
              />

              <TextField
                label="Website URL"
                value={form.websiteUrl}
                onChange={(e) => update('websiteUrl', e.target.value)}
                placeholder="Enter Website URL"
                error={errors.websiteUrl}
                testId={`${testPrefix}-website`}
                errorTestId={`${testPrefix}-website-error`}
              />

            </div>

            <div style={{ paddingBottom: '16px' }}>
              <MarkdownEditorField
                label={"Description"}
                value={form.description}
                placeholder={"Enter description"}
                onChange={(e) => update('description', e)}
                error={errors.description}
                testId={`${testPrefix}-description`}
              />
            </div>

            <DialogFooter>
          <button className="button secondary" onClick={close} data-testid={`${testPrefix}-cancel`}>Cancel</button>
<button className="button" onClick={handleSave} data-testid={`${testPrefix}-save`} disabled={logo.isPending}>Save Brand</button>
            </DialogFooter>

          </div>
        </div>

        <BrandCreationPreview form={{ ...form, brandLogoUrl: logo.url }} />
      </div>
    </div>
  );
}
